import { Link } from 'react-router-dom'

import { setJournalPref, useJournalPrefs } from '../lib/journal-prefs'
import { Section } from '../components/connections/parts'
import { ApiPath, NoteRow, OptionTile, SettingsScreen } from '../components/settings/parts'

// ============================================================================
// JOURNAL DEFAULTS — two settings, and both of them actually do something.
//
// NO MOCKUP SOURCE. The rule that decided the scope: a settings screen may only
// contain controls that are wired. There is no server-side settings store in
// LifeOS, so every candidate default was sorted into "can be a browser
// preference like the theme" or "would need a table, a route and a migration" —
// and only the first kind is here. The second kind is named at the bottom rather
// than rendered as a toggle nobody has connected.
//
// Neither of these hides data. The prompt still exists on the entry and still
// shows on the day page; a mood already recorded is still recorded and still
// visible everywhere it was. They hide CHROME on the composer, which is the only
// thing a browser-local preference is allowed to do.
//
// The composer is the home of the product (CLAUDE.md → "the journal is the home;
// features that add friction to writing lose"), which is exactly why the two
// things worth turning off are the two things on it that are not the text box.
// ============================================================================

interface Choice {
  value: 'shown' | 'hidden'
  label: string
  detail: string
}

const PROMPT_CHOICES: Choice[] = [
  { value: 'shown', label: 'Shown', detail: 'The day’s question sits above the box.' },
  { value: 'hidden', label: 'Hidden', detail: 'A blank page. The prompt is still on the entry.' },
]

const MOOD_CHOICES: Choice[] = [
  { value: 'shown', label: 'Shown', detail: 'Two rows of five dots under the text.' },
  { value: 'hidden', label: 'Hidden', detail: 'Nothing to skip. Past numbers are untouched.' },
]

export function JournalDefaults() {
  const prefs = useJournalPrefs()

  return (
    <SettingsScreen
      active="journal"
      title="Journal defaults"
      subtitle="What the composer shows you before you start typing"
    >
      <Section label="Daily prompt">
        <ChoiceRow
          name="journal-daily-prompt"
          choices={PROMPT_CHOICES}
          on={prefs.dailyPrompt}
          onPick={(on) => setJournalPref('dailyPrompt', on)}
        />
        <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
          The prompt is picked from the date itself and never from what you wrote — the same
          question every year on this day. It has always been a suggestion rather than a field, and
          hiding it here is the strongest way of saying so. The entry keeps whatever prompt the day
          had, and the{' '}
          <Link to="/journal" className="underline underline-offset-2 hover:text-ink">
            Journal
          </Link>{' '}
          archive still shows it.
        </p>
      </Section>

      <Section label="Mood and energy">
        <ChoiceRow
          name="journal-mood-energy"
          choices={MOOD_CHOICES}
          on={prefs.moodEnergy}
          onPick={(on) => setJournalPref('moodEnergy', on)}
        />
        <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
          Both rows were already skippable — an unset day stays unset and nothing ever asks twice.
          Hiding them removes the two rows from the composer and writes nothing: numbers you have
          already recorded stay on their entries, stay in{' '}
          {/* The braces go through a string literal rather than a numeric HTML entity: an
              entity like the one for `{` is hash-plus-digits, which the ship gate's brand
              audit reads as a raw hex colour. */}
          <ApiPath>{'GET /api/journal/{date}'}</ApiPath>, and
          come back the moment you switch this to Shown.
        </p>
      </Section>

      <Section label="Where these are kept">
        <NoteRow>
          In this browser only, next to your theme and UI size — nothing about them reaches the
          server, they do not follow you to your phone, and signing out does not clear them. That
          is a limit, stated rather than hidden: there is no settings table in LifeOS, and adding
          one so that two view preferences could sync would be a migration, a route and a contract
          for something already correct where you write.
        </NoteRow>
      </Section>

      <Section label="What is not here">
        <NoteRow>
          A default autosave delay, a starting tag set, a reminder to write, a per-day template —
          none of them exist and none of them is a toggle on this screen. Each would need somewhere
          on the server to live and something on the server to read it, and a control that stores a
          preference nothing consults is the same lie as a button that does nothing. Autosave runs
          on a fixed delay and flushes when you leave the page; tagging from the composer is not
          built, which the composer itself says.
        </NoteRow>
      </Section>
    </SettingsScreen>
  )
}

/** The Appearance screen's tile row: a real radiogroup, two options, arrow-key reachable. */
function ChoiceRow({
  name,
  choices,
  on,
  onPick,
}: {
  name: string
  choices: Choice[]
  on: boolean
  onPick: (on: boolean) => void
}) {
  return (
    <div
      role="radiogroup"
      aria-label={name === 'journal-daily-prompt' ? 'Daily prompt' : 'Mood and energy'}
      className="flex gap-3 rounded-card border border-line bg-surface p-3 shadow-card"
    >
      {choices.map((choice) => (
        <OptionTile
          key={choice.value}
          name={name}
          value={choice.value}
          label={choice.label}
          detail={choice.detail}
          selected={on === (choice.value === 'shown')}
          onPick={() => onPick(choice.value === 'shown')}
        />
      ))}
    </div>
  )
}
